import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { useCreateRecordFolderAssignment } from './use-create-record-folder-assignment';
import { useDeleteRecordFolderAssignment } from './use-delete-record-folder-assignment';
import { useRecordFolderAssignments } from './use-record-folder-assignments';

export const MACRO_ASSIGNMENT_ENTITIES = ['Macro'];

interface UseMacroFolderAssignmentsOptions {
  partnerIds?: number[];
}

interface SetMacroFolderAssignmentInput {
  macroId: string;
  folderId: string | null;
  title: string;
  partnerId?: number;
}

export const useMacroFolderAssignments = (options?: UseMacroFolderAssignmentsOptions) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();
  const partnerIds = options?.partnerIds;

  const { recordFolderAssignments, isLoading: assignmentsLoading } =
    useRecordFolderAssignments({
      entityFilter: MACRO_ASSIGNMENT_ENTITIES,
    });
  const { createRecordFolderAssignment, isLoading: createLoading } =
    useCreateRecordFolderAssignment({
      entityFilter: MACRO_ASSIGNMENT_ENTITIES,
    });
  const { deleteRecordFolderAssignment, isLoading: deleteLoading } =
    useDeleteRecordFolderAssignment();

  const fallbackPartnerId = isNullish(selectedPartnerId)
    ? PLATFORM_PARTNER_ID
    : selectedPartnerId;

  const filteredAssignments = useMemo(() => {
    if (!partnerIds?.length) {
      return recordFolderAssignments;
    }

    return recordFolderAssignments.filter((assignment) =>
      partnerIds.includes(assignment.partnerId),
    );
  }, [partnerIds, recordFolderAssignments]);

  const getAssignmentsForMacro = useCallback(
    (macroId: string) =>
      filteredAssignments.filter((assignment) => assignment.entityId === macroId),
    [filteredAssignments],
  );

  const setMacroFolderAssignment = useCallback(
    async ({ macroId, folderId, title, partnerId }: SetMacroFolderAssignmentInput) => {
      const currentAssignments = getAssignmentsForMacro(macroId);
      const hasMatchingFolder =
        !!folderId &&
        currentAssignments.length === 1 &&
        currentAssignments[0].recordFolderId === folderId;

      if (hasMatchingFolder) {
        return;
      }

      const resolvedPartnerId = isNullish(partnerId) ? fallbackPartnerId : partnerId;
      const omitIsolationModeHeader = resolvedPartnerId === PLATFORM_PARTNER_ID;

      try {
        if (folderId) {
          await createRecordFolderAssignment(
            {
              recordFolderId: folderId,
              entity: 'Macro',
              entityId: macroId,
              partnerId: resolvedPartnerId,
              sortOrder: 0,
              title,
            },
            { omitIsolationModeHeader },
          );
        }

        await Promise.all(
          currentAssignments.map((assignment) =>
            deleteRecordFolderAssignment(assignment, {
              omitIsolationModeHeader: assignment.partnerId === PLATFORM_PARTNER_ID,
            }),
          ),
        );
      } catch (error) {
        console.error(error);
        showErrorAlert('Something went wrong updating the macro folder. Please try again.');
      }
    },
    [
      createRecordFolderAssignment,
      deleteRecordFolderAssignment,
      fallbackPartnerId,
      getAssignmentsForMacro,
      showErrorAlert,
    ],
  );

  return {
    recordFolderAssignments: filteredAssignments,
    getAssignmentsForMacro,
    setMacroFolderAssignment,
    isLoading: assignmentsLoading || createLoading || deleteLoading,
  };
};
